import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';

export const CartPage = () => {
  const { items, updateQuantity, removeItem, total, clearCart } = useCart();

  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="empty-cart">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-[#E5DAC8] mx-auto mb-4" />
          <h2 className="text-2xl text-[#1A3625] mb-4">Sepetiniz Boş</h2>
          <p className="text-[#5C5C5C] mb-8">Henüz sepetinize ürün eklemediniz.</p>
          <Link to="/market" className="btn-primary">
            Alışverişe Başla
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page">
      {/* Header */}
      <section className="py-12 bg-white border-b border-[#E5DAC8]">
        <div className="container-custom">
          <h1 className="text-3xl md:text-4xl text-[#1A3625]">Sepetim</h1>
          <p className="text-[#5C5C5C] mt-2">{items.length} ürün</p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {items.map((item, index) => (
                <motion.div
                  key={item.id}
                  className="card-premium flex flex-col sm:flex-row gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`cart-item-${item.id}`}
                >
                  <Link to={`/market/urun/${item.id}`} className="flex-shrink-0">
                    <div className="w-full sm:w-32 h-32 rounded-2xl overflow-hidden">
                      <img 
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </Link>
                  
                  <div className="flex-1">
                    <Link to={`/market/urun/${item.id}`}>
                      <h3 className="text-lg font-medium text-[#1A3625] hover:text-[#D4AF37] transition-colors">
                        {item.name}
                      </h3>
                    </Link>
                    <p className="text-base text-[#5C5C5C] mb-4">{item.unit}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 rounded-full bg-[#F9F8F6] flex items-center justify-center hover:bg-[#E5DAC8] transition-colors"
                          data-testid={`decrease-${item.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 rounded-full bg-[#F9F8F6] flex items-center justify-center hover:bg-[#E5DAC8] transition-colors"
                          data-testid={`increase-${item.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <span className="font-semibold text-[#D4AF37]">
                          ₺{(item.price * item.quantity).toLocaleString()}
                        </span>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                          data-testid={`remove-${item.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
              
              <button
                onClick={clearCart}
                className="text-base text-red-500 hover:text-red-600 transition-colors"
                data-testid="clear-cart"
              >
                Sepeti Temizle
              </button>
            </div>

            {/* Summary */}
            <div className="lg:col-span-1">
              <div className="card-premium sticky top-28">
                <h3 className="text-xl font-medium text-[#1A3625] mb-6">Sipariş Özeti</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-[#5C5C5C]">
                    <span>Ara Toplam</span>
                    <span>₺{total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-[#5C5C5C]">
                    <span>Kargo</span>
                    <span>{total >= 500 ? 'Ücretsiz' : '₺50'}</span>
                  </div>
                  {total < 500 && (
                    <p className="text-sm text-[#D4AF37]">
                      ₺{500 - total} daha ekleyin, kargo ücretsiz olsun!
                    </p>
                  )}
                  <div className="border-t border-[#E5DAC8] pt-4">
                    <div className="flex justify-between text-lg font-semibold">
                      <span className="text-[#1A3625]">Toplam</span>
                      <span className="text-[#D4AF37]">
                        ₺{(total + (total >= 500 ? 0 : 50)).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
                
                <Link 
                  to="/market/siparis"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="checkout-button"
                >
                  Siparişi Tamamla
                  <ArrowRight className="w-5 h-5" />
                </Link>
                
                <Link 
                  to="/market"
                  className="block text-center text-[#1A3625] font-medium mt-4 hover:text-[#D4AF37] transition-colors"
                >
                  Alışverişe Devam Et
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
